import React, { useEffect, useMemo, useState } from "react";

import { fetchTransferPreview, requestShareTransfer } from "../../services/investmentService";
import { formatTransferApiError } from "../../utils/transferApiErrors";
import {
  formatTransferFeePctLabel,
  formatTransferLockInNote,
  formatTransferPreviewFeeBdt,
  formatTransferPreviewNavBdt,
  transferableSharesFromHolding,
  validateTransferSharesInput,
} from "../../utils/investorTransferShares";
import { formatShareQty } from "../../utils/investorRedeemEligibility";

/**
 * Shared transfer offer form — Transferable Projects modal and project card transfer tab.
 */
export default function InvestorTransferSharesForm({
  projectId,
  /** Net shares transferable (held minus pending redemption / other locks). */
  sharesAvailable: sharesAvailableProp,
  /** @deprecated Use sharesAvailable — kept for callers passing raw held only. */
  sharesHeld: sharesHeldLegacy,
  title = "Transfer shares",
  subtitle = "Transfer shares directly to another investor account. NAV and fees lock when you submit.",
  submitLabel = "Submit transfer",
  onSuccess,
  footerExtra = null,
  className = "stack invx-trade-card invx-trade-list",
}) {
  const projectIdStr = String(projectId || "").trim();
  const sharesAvailable = Number(
    sharesAvailableProp != null && sharesAvailableProp >= 0
      ? sharesAvailableProp
      : transferableSharesFromHolding({ shares_held: sharesHeldLegacy })
  );
  const [toInvestorId, setToInvestorId] = useState("");
  const [shares, setShares] = useState("1");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const validation = useMemo(() => validateTransferSharesInput(shares, sharesAvailable), [shares, sharesAvailable]);
  const hasRecipient = String(toInvestorId || "").trim() !== "";
  const needsRecipientPreview = hasRecipient && validation.valid;
  const previewLoading = needsRecipientPreview && !preview && !previewError;
  const previewReady = !needsRecipientPreview || Boolean(preview && !previewError);
  const transferSubmitBlocked = hasRecipient && Boolean(previewError);
  const displayError =
    apiError || previewError || (!validation.valid && shares !== "" ? validation.message : "");

  useEffect(() => {
    setToInvestorId("");
    setShares(sharesAvailable > 0 ? "1" : "");
    setPreview(null);
    setPreviewError("");
    setApiError("");
  }, [projectIdStr, sharesAvailable]);

  useEffect(() => {
    if (!projectIdStr) return undefined;
    const sharesNum = Number(shares);
    if (!Number.isFinite(sharesNum) || sharesNum <= 0) {
      setPreview(null);
      setPreviewError("");
      return undefined;
    }
    const local = validateTransferSharesInput(shares, sharesAvailable);
    if (!local.valid) {
      setPreview(null);
      setPreviewError("");
      return undefined;
    }
    const ac = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const rid = String(toInvestorId || "").trim();
        const { data } = await fetchTransferPreview(projectIdStr, sharesNum, rid || undefined);
        if (!ac.signal.aborted) {
          setPreview(data);
          setPreviewError("");
        }
      } catch (err) {
        if (!ac.signal.aborted) {
          setPreview(null);
          setPreviewError(formatTransferApiError(err));
        }
      }
    }, 350);
    return () => {
      ac.abort();
      window.clearTimeout(timer);
    };
  }, [projectIdStr, shares, toInvestorId, sharesAvailable]);

  const canSubmit =
    projectIdStr &&
    validation.valid &&
    hasRecipient &&
    previewReady &&
    !previewLoading &&
    !transferSubmitBlocked &&
    !submitting &&
    sharesAvailable > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setApiError("");
    setSubmitting(true);
    try {
      const { data } = await requestShareTransfer({
        project_id: projectIdStr,
        to_investor_id: String(toInvestorId).trim(),
        shares: Number(shares),
      });
      onSuccess?.(data);
    } catch (err) {
      setApiError(formatTransferApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={className} onSubmit={onSubmit}>
      {title ? <h3 className="invx-trade-title">{title}</h3> : null}
      {subtitle ? <p className="muted invx-trade-sub">{subtitle}</p> : null}
      <p className="muted" style={{ fontSize: 13, margin: "0 0 8px" }}>
        Available shares to transfer: <strong>{formatShareQty(sharesAvailable)}</strong>
      </p>
      <label className="muted" style={{ fontSize: 13, display: "block" }} htmlFor={`xfer-to-${projectIdStr}`}>
        Recipient (investor account # or user UUID)
      </label>
      <input
        id={`xfer-to-${projectIdStr}`}
        placeholder="e.g. 30 or paste UUID"
        value={toInvestorId}
        onChange={(e) => {
          setApiError("");
          setToInvestorId(e.target.value);
        }}
        required
      />
      <label className="muted" style={{ fontSize: 13, display: "block" }} htmlFor={`xfer-shares-${projectIdStr}`}>
        Number of shares
      </label>
      <input
        id={`xfer-shares-${projectIdStr}`}
        placeholder="Shares to transfer"
        type="number"
        min="0.01"
        step="0.01"
        max={sharesAvailable > 0 ? sharesAvailable : undefined}
        value={shares}
        onChange={(e) => {
          setApiError("");
          setShares(e.target.value);
        }}
        required
      />
      {preview && validation.valid && hasRecipient && !previewError ? (
        <div className="invx-transfer-preview invx-transfer-preview--compact" role="status">
          <dl className="invx-transfer-preview__dl">
            <div>
              <dt>Price per share</dt>
              <dd>{formatTransferPreviewNavBdt(preview)}</dd>
            </div>
            {preview.receiverDisplayName ? (
              <div>
                <dt>To</dt>
                <dd>{preview.receiverDisplayName}</dd>
              </div>
            ) : null}
            <div>
              <dt>Transfer fee</dt>
              <dd>
                {formatTransferPreviewFeeBdt(preview)}{" "}
                <span className="invx-transfer-preview__pct">({formatTransferFeePctLabel(preview.transferFeePct)})</span>
              </dd>
            </div>
          </dl>
          {preview.redemptionUnlockAt ? (
            <p className="invx-transfer-preview__note">{formatTransferLockInNote(preview.redemptionUnlockAt)}</p>
          ) : null}
        </div>
      ) : null}
      {previewLoading ? (
        <p className="muted" style={{ fontSize: 13, margin: "0 0 8px" }} role="status">
          Checking recipient and transfer details…
        </p>
      ) : null}
      {displayError ? (
        <p className="msg msg-error" role="alert">
          {displayError}
        </p>
      ) : null}
      {submitting ? (
        <p className="msg" role="status">
          Submitting transfer offer…
        </p>
      ) : null}
      <button type="submit" className="invx-trade-submit" disabled={!canSubmit} aria-busy={submitting}>
        {submitting ? "Submitting…" : submitLabel}
      </button>
      {footerExtra}
    </form>
  );
}
